import '../../../styles/restScreens.css';
import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import PhoneInput from '../../ui/PhoneInput';
import { isUnparsedPhone, phoneForSave } from '../../../lib/phoneSave';
import { toE164 } from '../../../lib/phone';
import { RSVP_STATUSES, rsvpLabel } from '../eventLabels';
import { DEFAULT_EVENT_SETTINGS, plusOnesDefault, rsvpChoices } from '../eventSettings';
import { BLANK_GUEST, DEFAULT_COUNTRY, PLUS_ONES_MAX, duplicateMessage, findDuplicate, guestName, sharedContactNotes } from './guestConstants';
import { DEFAULT_EVENT_WORDS } from '../eventWords';
import Switch from '../../ui/Switch';
import { ARRIVAL_MAX, arrivalProblem } from '../hotel/hotelBlock';
import EscapeGuard from './EscapeGuard';

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const SELECT_CLASS = 'mt-[3px] w-full min-h-[44px] lg:[@media(pointer:fine)]:min-h-[34px] rounded-lg border border-[#64748B] bg-[#0B1220] px-2 text-white';

// Add / edit a guest, or add a plus-one to a host. The platform popup template (Modal3D) with the Create-reservation tile's field look.
// Escape never closes it (M6 rule); the x and Cancel do.
export default function GuestFormPopup({ mode = 'add', guest = null, host = null, groups = [], guests = [], settings = DEFAULT_EVENT_SETTINGS, saving = false, error = '', onSave, onClose, words = DEFAULT_EVENT_WORDS }) {
    const plusOne = mode === 'plus-one' || Boolean(guest?.plus_one_of);
    const [form, setForm] = useState(() => ({
        ...BLANK_GUEST,
        // a new guest starts from the saved default plus-ones; this guest can override it without changing the setting
        plus_ones_allowed: plusOnesDefault(settings),
        ...(guest ? {
            first_name: guest.first_name || '', last_name: guest.last_name || '', email: guest.email || '', phone: guest.phone || '',
            group_id: guest.group_id || '', rsvp_status: guest.rsvp_status || 'pending', plus_ones_allowed: guest.plus_ones_allowed ?? 0, notes: guest.notes || '',
        } : {}),
        // G6: the shuttle question, only while it is on in Events Settings > Hotel & Travel
        needs_shuttle: guest?.needs_shuttle === true,
        arrival_info: guest?.arrival_info || '',
    }));
    const askShuttle = settings.shuttle_enabled !== false;
    const [problem, setProblem] = useState('');
    const set = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

    const title = mode === 'plus-one' ? 'Add plus-one' : guest ? 'Edit guest' : 'Add guest';
    const subtitle = mode === 'plus-one'
        ? `Guest of ${guestName(host)}.`
        : guest?.plus_one_of ? 'A plus-one of another guest.' : `Add someone to this ${words.eventLower}’s guest list.`;

    const submit = (event) => {
        event?.preventDefault?.();
        if (!form.first_name.trim()) return setProblem('Enter a first name.');
        if (form.email.trim() && !EMAIL.test(form.email.trim())) return setProblem('That email address does not look right.');
        if (form.phone.trim() && isUnparsedPhone(form.phone, DEFAULT_COUNTRY)) return setProblem('That phone number does not look right. Include the country code for numbers outside the US.');
        const plus = Number(form.plus_ones_allowed || 0);
        if (!plusOne && (!Number.isInteger(plus) || plus < 0 || plus > PLUS_ONES_MAX)) return setProblem(`Plus-ones allowed must be a number from 0 to ${PLUS_ONES_MAX}.`);
        const phone = form.phone.trim() ? phoneForSave(form.phone, DEFAULT_COUNTRY) : '';
        // the same guest twice (same name + same email or phone) is refused here in plain words; the database refuses it too
        const duplicate = findDuplicate({ ...form, phone }, guests, guest?.id);
        if (duplicate) return setProblem(duplicateMessage(duplicate.via));
        const arrival = askShuttle && form.needs_shuttle ? arrivalProblem(form.arrival_info) : '';
        if (arrival) return setProblem(arrival);
        setProblem('');
        // with the shuttle question off, the stored flag is left exactly as it was (never cleared by a hidden field)
        const { needs_shuttle: shuttle, arrival_info: arrivalInfo, ...rest } = form;
        onSave({ ...rest, phone, plus_ones_allowed: plusOne ? 0 : plus, ...(askShuttle ? { needs_shuttle: shuttle, arrival_info: arrivalInfo } : {}) });
    };

    const field = (id, label, control, wide = false) => (
        <li className={`ia-resv-field${wide ? ' ia-resv-field--wide' : ''}`}>
            <div className="ia-resv-field__label"><label htmlFor={id}>{label}</label></div>
            {control}
        </li>
    );
    const shown = problem || error;
    // a different guest with the same phone or email is fine (couples, families): say so, do not block
    const sharedNotes = sharedContactNotes({ ...form, phone: form.phone.trim() ? toE164(form.phone, DEFAULT_COUNTRY) || '' : '' }, guests, guest?.id);

    return (
        <EscapeGuard>
            <Modal3D
                title={title}
                subtitle={subtitle}
                testId="guest-form-popup"
                onClose={onClose}
                data-m6-layout="modals"
                data-m6-component="EventGuestFormPopup"
                actions={(
                    <>
                        <Button3D onClick={onClose} disabled={saving}>Cancel</Button3D>
                        <Button3D variant="go" onClick={submit} disabled={saving}>{saving ? 'Saving…' : mode === 'plus-one' ? 'Add plus-one' : guest ? 'Save guest' : 'Add guest'}</Button3D>
                    </>
                )}
            >
                <form onSubmit={submit} noValidate>
                    <ul className="ia-resv-grid" aria-label="Guest details">
                        {field('guest-first', 'First name', <input id="guest-first" value={form.first_name} onChange={set('first_name')} autoComplete="off" />)}
                        {field('guest-last', 'Last name', <input id="guest-last" value={form.last_name} onChange={set('last_name')} autoComplete="off" />)}
                        {field('guest-email', 'Email', <input id="guest-email" type="email" value={form.email} onChange={set('email')} autoComplete="off" />)}
                        {field('guest-phone', 'Phone', (
                            <PhoneInput id="guest-phone" value={form.phone} defaultCountry={DEFAULT_COUNTRY} onChange={(value) => setForm((previous) => ({ ...previous, phone: value }))} />
                        ))}
                        {field('guest-rsvp', 'RSVP', (
                            <select id="guest-rsvp" className={SELECT_CLASS} value={form.rsvp_status} onChange={set('rsvp_status')}>
                                {rsvpChoices(RSVP_STATUSES, settings, guest?.rsvp_status).map((status) => <option key={status} value={status}>{rsvpLabel(status)}</option>)}
                            </select>
                        ))}
                        {!plusOne && field('guest-group', 'Group', (
                            <select id="guest-group" className={SELECT_CLASS} value={form.group_id} onChange={set('group_id')}>
                                <option value="">No group</option>
                                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                            </select>
                        ))}
                        {!plusOne && (settings.allow_plus_ones || Number(guest?.plus_ones_allowed) > 0) && field('guest-plus', 'Plus-ones allowed', <input id="guest-plus" type="number" min="0" max={PLUS_ONES_MAX} inputMode="numeric" value={form.plus_ones_allowed} onChange={set('plus_ones_allowed')} />)}
                        {askShuttle && (
                            <li className="ia-resv-field ia-resv-field--wide" data-testid="guest-shuttle-field">
                                <div className="flex items-center justify-between gap-3">
                                    <span id="guest-shuttle-label" className="text-sm font-bold">Needs the shuttle</span>
                                    <Switch id="guest-shuttle" checked={form.needs_shuttle} onChange={(value) => setForm((previous) => ({ ...previous, needs_shuttle: value }))} aria-label="Needs the shuttle" describedBy="guest-shuttle-note" />
                                </div>
                                <p id="guest-shuttle-note" className="ia-resv-field__why">Counted on this {words.eventLower}. Shuttle trips are planned later; nothing is booked or sent now.</p>
                            </li>
                        )}
                        {askShuttle && form.needs_shuttle && field('guest-arrival', 'Arrival details (optional)', <input id="guest-arrival" maxLength={ARRIVAL_MAX} value={form.arrival_info} onChange={set('arrival_info')} placeholder="e.g. DL 1234, lands ATL 3:05 PM" autoComplete="off" />, true)}
                        {field('guest-notes', 'Notes', <textarea id="guest-notes" rows={2} value={form.notes} onChange={set('notes')} />, true)}
                    </ul>
                    {sharedNotes.map((note) => <p key={note} className="mt-2 text-sm" data-testid="guest-shared-note">{note} That is fine; couples and families often share one.</p>)}
                    {shown && <p className="ia-resv-error" role="alert">{shown}</p>}
                </form>
            </Modal3D>
        </EscapeGuard>
    );
}
